// Self-service change-password modal. Opened from the sidebar account block.
// Shows the currently-logged-in account at the top, then old / new / confirm
// password fields. Mirrors the dialog shell used by the employee dialogs.
//
// The backend enforces only a non-empty new password (no length policy — matches
// the login side); the client additionally requires new === confirm as UX.

import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { toast } from "sonner";
import { KeyRound } from "lucide-react";
import { useI18n } from "@faceheart/i18n";
import { useAuth } from "../lib/auth/AuthProvider";
import { authApi } from "../lib/api/endpoints";
import { ApiError } from "../lib/api/client";

export function ChangePasswordDialog({ onClose }: { onClose: () => void }) {
  const { t } = useI18n("admin");
  const { user } = useAuth();
  const [oldPw, setOldPw] = useState("");
  const [newPw, setNewPw] = useState("");
  const [confirmPw, setConfirmPw] = useState("");

  const changeMut = useMutation({
    mutationFn: () => authApi.changePassword(oldPw, newPw),
    onSuccess: () => {
      toast.success(t("settings.account.success"));
      onClose();
    },
    onError: (err) => toast.error(accountError(t, err)),
  });

  const canSubmit =
    oldPw.length > 0 && newPw.length > 0 && newPw === confirmPw && !changeMut.isPending;

  const inputCls =
    "w-full bg-admin-bg1 border border-admin-border rounded px-3 py-2 text-sm text-admin-text1";

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      onClick={onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label={t("settings.account.title")}
        className="w-96 max-w-[92vw] rounded-lg border border-admin-border bg-admin-bg2 p-6 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-4 flex items-start justify-between">
          <h2 className="text-lg font-medium flex items-center gap-2">
            <KeyRound className="w-4 h-4" /> {t("settings.account.title")}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="text-admin-text3 hover:text-admin-text1"
            aria-label={t("common:actions.close")}
          >
            ✕
          </button>
        </div>

        {/* Currently-logged-in account. */}
        <div className="mb-4 rounded border border-admin-border bg-admin-bg1 px-3 py-2 text-sm">
          <div className="text-xs text-admin-text3">{t("settings.account.currentAccount")}</div>
          <div className="text-admin-text1 truncate">{user?.email ?? "—"}</div>
        </div>

        <form
          className="space-y-3"
          onSubmit={(e) => {
            e.preventDefault();
            if (canSubmit) changeMut.mutate();
          }}
        >
          <div>
            <label className="block text-xs text-admin-text2 mb-1">
              {t("settings.account.oldLabel")}
            </label>
            <input
              type="password"
              autoComplete="current-password"
              className={inputCls}
              value={oldPw}
              onChange={(e) => setOldPw(e.target.value)}
              autoFocus
            />
          </div>
          <div>
            <label className="block text-xs text-admin-text2 mb-1">
              {t("settings.account.newLabel")}
            </label>
            <input
              type="password"
              autoComplete="new-password"
              className={inputCls}
              value={newPw}
              onChange={(e) => setNewPw(e.target.value)}
            />
          </div>
          <div>
            <label className="block text-xs text-admin-text2 mb-1">
              {t("settings.account.confirmLabel")}
            </label>
            <input
              type="password"
              autoComplete="new-password"
              className={inputCls}
              value={confirmPw}
              onChange={(e) => setConfirmPw(e.target.value)}
            />
            {confirmPw.length > 0 && newPw !== confirmPw && (
              <p className="text-xs text-admin-red mt-1">{t("settings.account.mismatch")}</p>
            )}
          </div>
          <div className="flex justify-end gap-2 pt-1">
            <button
              type="button"
              onClick={onClose}
              className="rounded border border-admin-border px-4 py-2 text-sm text-admin-text2 hover:text-admin-text1"
            >
              {t("common:actions.cancel")}
            </button>
            <button
              type="submit"
              disabled={!canSubmit}
              className="rounded bg-admin-cyan/20 border border-admin-cyan/40 text-admin-cyan px-4 py-2 text-sm hover:bg-admin-cyan/30 disabled:opacity-50"
            >
              {t("settings.account.submit")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// accountError maps a change-password API error code to a localized message.
function accountError(t: ReturnType<typeof useI18n>["t"], err: unknown): string {
  const code = err instanceof ApiError ? err.code : "internal";
  switch (code) {
    case "invalid_credentials":
      return t("settings.account.errOldWrong");
    case "rate_limited":
      return t("settings.account.errRateLimited");
    case "invalid_request":
      return t("settings.account.errInvalid");
    default:
      return t("settings.account.errGeneric");
  }
}
